/**
 * Save a file the server holds.
 *
 * Two mechanisms, because two kinds of URL:
 *
 * - **Fetched** (`/api/` routes, or any caller passing `filename`): the bytes come
 *   back through axios and are handed to a real `<a download>`. This is the one
 *   that can *fail out loud* — the caller gets a rejected promise — and the one
 *   that names the file itself.
 * - **Navigated** (a hidden iframe): for session-authenticated routes and exports
 *   that may be very large, where buffering the whole response in memory is not
 *   acceptable. Nothing can be observed about it: a 404, a 403 and a silent
 *   cache hit all look exactly the same from here, which is why a download the
 *   person needs confirmed should pass a `filename` and take the first path.
 *
 * @param {string} url
 * @param {{filename?: string}} [options] a filename forces the fetched path, and
 *   is used when the response names none
 * @returns {Promise<void>} resolves once the file has been handed to the browser
 */
export default function (url, options = {}) {
    if (url.includes('/api/') || options.filename) {
        return axios.get(url, { responseType: 'blob' }).then(response => {
            const disposition = response.headers['content-disposition'];
            let filename = options.filename || 'download';
            if (disposition) {
                const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
                if (match) {
                    filename = match[1];
                }
            }

            const blob = new Blob([response.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        });
    }

    let hiddenIFrameID = 'hiddenDownloader',
        iframe = document.getElementById(hiddenIFrameID);
    if (iframe === null) {
        iframe = document.createElement('iframe');
        iframe.id = hiddenIFrameID;
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
    }

    iframe.src = url;

    return Promise.resolve();
}
